// src/pages/vendor/Orders.jsx - AUTO GENERATE TRACKING
import React, { useEffect, useState } from "react";
import { useVendor } from "../../hooks/useVendor";
import { settingsApi } from "../../api/settingsApi";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Grid,
  Card,
  CardContent,
  Divider,
  useMediaQuery,
  useTheme,
  Collapse,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Search,
  MoreVert,
  Visibility,
  LocalShipping,
  CheckCircle,
  Cancel,
  Pending,
  Refresh,
  Download,
  ShoppingBag,
  Person,
  ExpandMore,
  ExpandLess,
  Email,
  Phone,
  PictureAsPdf,
} from "@mui/icons-material";

const Orders = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { orders, loading, error, fetchOrders, updateOrderStatus } =
    useVendor();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  // NEW: Ship Order Dialog State
  const [openShipDialog, setOpenShipDialog] = useState(false);
  const [logisticsCompanies, setLogisticsCompanies] = useState([]);
  const [shipForm, setShipForm] = useState({ shippingProvider: "" });
  const [isAssigningShipment, setIsAssigningShipment] = useState(false);
  const [shipError, setShipError] = useState(null);

  useEffect(() => {
    fetchOrders();
    fetchLogisticsCompanies();
  }, []);

  const fetchLogisticsCompanies = async () => {
    try {
      const response = await settingsApi.getLogisticsPricing();
      let companies = [];
      if (response?.data?.data) companies = response.data.data;
      else if (response?.data) companies = response.data;
      else if (Array.isArray(response)) companies = response;
      setLogisticsCompanies(
        companies.filter(
          (c) => c.isActive === true || c.isActive === 1 || c.isActive === "1",
        ),
      );
    } catch (err) {
      console.error("Failed to fetch logistics companies:", err);
    }
  };

  const handleSearch = (e) => setSearchTerm(e.target.value);
  const handleFilterChange = (status) => setFilterStatus(status);
  const handleMenuOpen = (event, order) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };
  const handleMenuClose = () => setAnchorEl(null);
  const handleViewDetails = () => {
    setOpenDetails(true);
    handleMenuClose();
  };
  const handleExpandOrder = (orderId) =>
    setExpandedOrder(expandedOrder === orderId ? null : orderId);

  const handleOpenShipDialog = () => {
    if (selectedOrder)
      setShipForm({ shippingProvider: selectedOrder.shippingProvider || "" });
    setOpenShipDialog(true);
    handleMenuClose();
  };

  // ✅ NEW: AUTO GENERATES TRACKING NUMBER ON BACKEND!
  const handleShipOrder = async () => {
    if (!shipForm.shippingProvider) {
      setShipError("Please select a logistics company.");
      return;
    }
    setIsAssigningShipment(true);
    setShipError(null);

    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "https://api.goscitech.com"}/logistics/create-shipment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            orderId: selectedOrder.id,
            company: shipForm.shippingProvider,
          }),
        },
      );
      const data = await response.json();
      if (data.success) {
        setOpenShipDialog(false);
        fetchOrders();
        alert(`✅ Shipment created! Tracking Number: ${data.trackingNumber}`);
      } else setShipError(data.message || "Failed to assign shipment");
    } catch (error) {
      setShipError(
        error.response?.data?.message || "Failed to assign shipment",
      );
    } finally {
      setIsAssigningShipment(false);
    }
  };

  const handleStatusUpdate = async (status) => {
    if (selectedOrder) {
      await updateOrderStatus(selectedOrder.id, status);
      fetchOrders();
      handleMenuClose();
    }
  };

  const handleViewInvoice = async (orderId) => {
    setLoadingInvoice(true);
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        alert("Please login to view invoice");
        return;
      }
      const baseUrl =
        process.env.REACT_APP_API_URL || "https://api.goscitech.com";
      const url = `${baseUrl}/orders/${orderId}/invoice`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/pdf",
        },
      });
      if (!response.ok)
        throw new Error(`Failed to download invoice (${response.status})`);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `invoice-${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      alert(error.message || "Failed to download invoice");
    } finally {
      setLoadingInvoice(false);
    }
  };

  const filteredOrders = orders?.filter((order) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      order.orderNumber?.toLowerCase().includes(searchLower) ||
      order.User?.name?.toLowerCase().includes(searchLower);
    const matchesStatus =
      filterStatus === "all" ||
      order.status?.toLowerCase() === filterStatus.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "warning";
      case "processing":
        return "info";
      case "shipped":
        return "primary";
      case "delivered":
        return "success";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return <Pending />;
      case "processing":
        return <Refresh />;
      case "shipped":
        return <LocalShipping />;
      case "delivered":
        return <CheckCircle />;
      case "cancelled":
        return <Cancel />;
      default:
        return <Pending />;
    }
  };
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const getAvailableActions = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return ["processing", "cancelled"];
      case "processing":
        return ["shipped", "cancelled"];
      case "shipped":
        return ["delivered"];
      case "delivered":
        return [];
      case "cancelled":
        return [];
      default:
        return [];
    }
  };
  const getActionLabel = (status) => {
    switch (status) {
      case "processing":
        return "Process";
      case "shipped":
        return "Ship";
      case "delivered":
        return "Deliver";
      case "cancelled":
        return "Cancel";
      default:
        return status;
    }
  };
  const getActionIcon = (status) => {
    switch (status) {
      case "processing":
        return <Refresh fontSize="small" sx={{ color: "info.main" }} />;
      case "shipped":
        return (
          <LocalShipping fontSize="small" sx={{ color: "primary.main" }} />
        );
      case "delivered":
        return <CheckCircle fontSize="small" sx={{ color: "success.main" }} />;
      case "cancelled":
        return <Cancel fontSize="small" sx={{ color: "error.main" }} />;
      default:
        return null;
    }
  };

  const renderMobileOrderCard = (order) => {
    const isExpanded = expandedOrder === order.id;
    const availableActions = getAvailableActions(order.status);
    const canViewInvoice = ["processing", "shipped", "delivered"].includes(
      order.status?.toLowerCase(),
    );

    return (
      <Card key={order.id} sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  #{order.orderNumber || order.id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(order.createdAt)}
                </Typography>
              </Box>
              <Chip
                icon={getStatusIcon(order.status)}
                label={order.status || "Pending"}
                size="small"
                color={getStatusColor(order.status)}
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: "primary.main",
                  fontSize: 12,
                }}
              >
                {order.User?.name?.[0]?.toUpperCase() || "U"}
              </Avatar>
              <Typography variant="body2">
                {order.User?.name || "Unknown"}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {order.OrderItems?.length || 0} items
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="primary">
                ${order.totalAmount?.toFixed(2) || "0.00"}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {availableActions.map((action) => (
                <Button
                  key={action}
                  size="small"
                  variant="outlined"
                  startIcon={getActionIcon(action)}
                  onClick={() => {
                    setSelectedOrder(order);
                    handleStatusUpdate(action);
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.7rem",
                    flex: isMobile ? 1 : "auto",
                  }}
                >
                  {getActionLabel(action)}
                </Button>
              ))}
              {order.status?.toLowerCase() === "processing" && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<LocalShipping />}
                  onClick={() => {
                    setSelectedOrder(order);
                    handleOpenShipDialog();
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.7rem",
                    flex: isMobile ? 1 : "auto",
                  }}
                >
                  Assign Shipment
                </Button>
              )}
              {canViewInvoice && (
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<PictureAsPdf />}
                  onClick={() => handleViewInvoice(order.id)}
                  disabled={loadingInvoice}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.7rem",
                    flex: isMobile ? 1 : "auto",
                  }}
                >
                  Invoice
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                startIcon={<Visibility />}
                onClick={() => {
                  setSelectedOrder(order);
                  setOpenDetails(true);
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "0.7rem",
                  flex: isMobile ? 1 : "auto",
                }}
              >
                View
              </Button>
              <IconButton
                size="small"
                onClick={() => handleExpandOrder(order.id)}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Stack>
            <Collapse in={isExpanded}>
              <Divider sx={{ my: 1 }} />
              <Stack spacing={1}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Customer Details
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Email fontSize="small" color="action" />
                  <Typography variant="body2">
                    {order.User?.email || "N/A"}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Phone fontSize="small" color="action" />
                  <Typography variant="body2">
                    {order.User?.phone || "N/A"}
                  </Typography>
                </Stack>
                <Divider />
                <Typography variant="subtitle2" fontWeight="bold">
                  Items
                </Typography>
                {order.OrderItems?.map((item) => (
                  <Stack
                    key={item.id}
                    direction="row"
                    justifyContent="space-between"
                    sx={{ py: 0.5 }}
                  >
                    <Typography variant="body2">
                      {item.Product?.name || "Unknown"}
                      {item.size && ` (${item.size})`}
                    </Typography>
                    <Typography variant="body2">
                      {item.quantity} × ${item.price?.toFixed(2) || "0.00"}
                    </Typography>
                  </Stack>
                ))}
                {order.trackingNumber && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <strong>Tracking:</strong> {order.trackingNumber} (
                    {order.shippingProvider || order.shippingCarrier})
                  </Alert>
                )}
              </Stack>
            </Collapse>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Orders
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and track your customer orders
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} width={{ xs: "100%", sm: "auto" }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => fetchOrders()}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Export
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: "center", p: { xs: 1.5, sm: 2 } }}>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                {orders?.length || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Orders
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: "center", p: { xs: 1.5, sm: 2 } }}>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                fontWeight="bold"
                color="warning.main"
              >
                {orders?.filter((o) => o.status === "pending").length || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pending
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: "center", p: { xs: 1.5, sm: 2 } }}>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                fontWeight="bold"
                color="success.main"
              >
                {orders?.filter((o) => o.status === "delivered").length || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Delivered
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: "center", p: { xs: 1.5, sm: 2 } }}>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                fontWeight="bold"
                color="error.main"
              >
                {orders?.filter((o) => o.status === "cancelled").length || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Cancelled
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={isMobile ? 1 : 2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            placeholder="Search by order or customer..."
            value={searchTerm}
            onChange={handleSearch}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            <Chip
              label="All"
              onClick={() => handleFilterChange("all")}
              color={filterStatus === "all" ? "primary" : "default"}
              clickable
              size="small"
            />
            <Chip
              label="Pending"
              onClick={() => handleFilterChange("pending")}
              color={filterStatus === "pending" ? "warning" : "default"}
              clickable
              size="small"
            />
            <Chip
              label="Processing"
              onClick={() => handleFilterChange("processing")}
              color={filterStatus === "processing" ? "info" : "default"}
              clickable
              size="small"
            />
            <Chip
              label="Shipped"
              onClick={() => handleFilterChange("shipped")}
              color={filterStatus === "shipped" ? "primary" : "default"}
              clickable
              size="small"
            />
            <Chip
              label="Delivered"
              onClick={() => handleFilterChange("delivered")}
              color={filterStatus === "delivered" ? "success" : "default"}
              clickable
              size="small"
            />
            <Chip
              label="Cancelled"
              onClick={() => handleFilterChange("cancelled")}
              color={filterStatus === "cancelled" ? "error" : "default"}
              clickable
              size="small"
            />
          </Box>
        </Stack>
      </Paper>

      {isMobile ? (
        <Box>
          {filteredOrders?.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
              <ShoppingBag
                sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
              />
              <Typography color="text.secondary">No orders found</Typography>
            </Paper>
          ) : (
            filteredOrders.map((order) => renderMobileOrderCard(order))
          )}
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: "grey.50" }}>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Order
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Customer
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Items
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Total
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Date
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Status
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight="bold">
                      Actions
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                      <Stack alignItems="center" spacing={1}>
                        <ShoppingBag
                          sx={{ fontSize: 48, color: "text.secondary" }}
                        />
                        <Typography color="text.secondary">
                          No orders found
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          #{order.orderNumber || order.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              bgcolor: "primary.main",
                              fontSize: 12,
                            }}
                          >
                            {order.User?.name?.[0]?.toUpperCase() || "U"}
                          </Avatar>
                          <Typography variant="body2">
                            {order.User?.name || "Unknown"}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.OrderItems?.length || 0} items
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="600"
                          color="primary"
                        >
                          ${order.totalAmount?.toFixed(2) || "0.00"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(order.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(order.status)}
                          label={order.status || "Pending"}
                          size="small"
                          color={getStatusColor(order.status)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, order)}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <Visibility fontSize="small" sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <Divider />
        {getAvailableActions(selectedOrder?.status).map((action) => (
          <MenuItem
            key={action}
            onClick={() => handleStatusUpdate(action)}
            sx={{ color: action === "cancelled" ? "error.main" : undefined }}
          >
            {getActionIcon(action)}
            {getActionLabel(action)} Order
          </MenuItem>
        ))}
        {selectedOrder?.status?.toLowerCase() === "processing" && (
          <MenuItem
            onClick={handleOpenShipDialog}
            sx={{ color: "primary.main" }}
          >
            <LocalShipping fontSize="small" sx={{ mr: 1 }} /> Assign Shipment
          </MenuItem>
        )}
        {selectedOrder?.status === "delivered" && (
          <MenuItem disabled>
            <CheckCircle
              fontSize="small"
              sx={{ mr: 1, color: "success.main" }}
            />{" "}
            Order Completed
          </MenuItem>
        )}
        {selectedOrder?.status === "cancelled" && (
          <MenuItem disabled>
            <Cancel fontSize="small" sx={{ mr: 1, color: "error.main" }} />{" "}
            Order Cancelled
          </MenuItem>
        )}
        {selectedOrder &&
          ["processing", "shipped", "delivered"].includes(
            selectedOrder.status?.toLowerCase(),
          ) && (
            <MenuItem onClick={() => handleViewInvoice(selectedOrder.id)}>
              <PictureAsPdf
                fontSize="small"
                sx={{ mr: 1, color: "error.main" }}
              />{" "}
              View Invoice
            </MenuItem>
          )}
      </Menu>

      <Dialog
        open={openShipDialog}
        onClose={() => setOpenShipDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LocalShipping color="primary" />
            <Typography variant="h6">Assign Shipment</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Order:{" "}
              <strong>
                #{selectedOrder?.orderNumber || selectedOrder?.id}
              </strong>{" "}
              - {selectedOrder?.User?.name || "Customer"}
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Logistics Company</InputLabel>
              <Select
                value={shipForm.shippingProvider}
                onChange={(e) =>
                  setShipForm({ shippingProvider: e.target.value })
                }
                label="Logistics Company"
                required
              >
                {logisticsCompanies.length > 0 ? (
                  logisticsCompanies.map((company) => (
                    <MenuItem
                      key={company.id || company.company}
                      value={company.company}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <LocalShipping fontSize="small" />
                        <span>
                          {company.company?.replace("_", " ").toUpperCase()}
                        </span>
                        {company.isActive && (
                          <Chip label="Active" size="small" color="success" />
                        )}
                      </Stack>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">No logistics companies available</MenuItem>
                )}
              </Select>
            </FormControl>
            <Alert severity="info">
              The system will automatically generate a tracking number for you.
              No manual entry required.
            </Alert>
            {shipError && <Alert severity="error">{shipError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button onClick={() => setOpenShipDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={
              isAssigningShipment ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <LocalShipping />
              )
            }
            onClick={handleShipOrder}
            disabled={isAssigningShipment || !shipForm.shippingProvider}
            fullWidth={isMobile}
          >
            {isAssigningShipment ? "Assigning..." : "Assign & Mark as Shipped"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, m: { xs: 1, sm: 2 } } }}
      >
        <DialogTitle>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="h6">
              Order #{selectedOrder?.orderNumber || selectedOrder?.id}
            </Typography>
            <Chip
              icon={getStatusIcon(selectedOrder?.status)}
              label={selectedOrder?.status || "Pending"}
              size="small"
              color={getStatusColor(selectedOrder?.status)}
            />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Stack spacing={3}>
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  <Person
                    fontSize="small"
                    sx={{ mr: 0.5, verticalAlign: "middle" }}
                  />{" "}
                  Customer Information
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={2}
                >
                  <Avatar sx={{ bgcolor: "primary.main" }}>
                    {selectedOrder.User?.name?.[0]?.toUpperCase() || "U"}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight="600">
                      {selectedOrder.User?.name || "Unknown"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedOrder.User?.email || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedOrder.User?.phone || "N/A"}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  <ShoppingBag
                    fontSize="small"
                    sx={{ mr: 0.5, verticalAlign: "middle" }}
                  />{" "}
                  Order Items
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.OrderItems?.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Avatar
                                variant="rounded"
                                src={item.Product?.thumbnail}
                                sx={{ width: 28, height: 28 }}
                              >
                                <ShoppingBag />
                              </Avatar>
                              <Typography variant="body2">
                                {item.Product?.name || "Unknown"}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">
                            ${item.price?.toFixed(2) || "0.00"}
                          </TableCell>
                          <TableCell align="right" fontWeight="600">
                            $
                            {(item.price * item.quantity)?.toFixed(2) || "0.00"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  <CheckCircle
                    fontSize="small"
                    sx={{ mr: 0.5, verticalAlign: "middle" }}
                  />{" "}
                  Order Summary
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Subtotal</Typography>
                    <Typography variant="body2">
                      ${selectedOrder.subtotal?.toFixed(2) || "0.00"}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Shipping</Typography>
                    <Typography variant="body2">
                      ${selectedOrder.shippingCost?.toFixed(2) || "0.00"}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Tax</Typography>
                    <Typography variant="body2">
                      ${selectedOrder.tax?.toFixed(2) || "0.00"}
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body1" fontWeight="bold">
                      Total
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color="primary"
                    >
                      ${selectedOrder.totalAmount?.toFixed(2) || "0.00"}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
              {selectedOrder.trackingNumber && (
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: "#e3f2fd" }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    color="primary.main"
                    sx={{ mb: 1 }}
                  >
                    <LocalShipping
                      fontSize="small"
                      sx={{ mr: 0.5, verticalAlign: "middle" }}
                    />{" "}
                    Tracking Information
                  </Typography>
                  <Typography variant="body2">
                    <strong>Tracking #:</strong> {selectedOrder.trackingNumber}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Provider:</strong>{" "}
                    {selectedOrder.shippingProvider ||
                      selectedOrder.shippingCarrier}
                  </Typography>
                </Paper>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button onClick={() => setOpenDetails(false)} fullWidth={isMobile}>
            Close
          </Button>
          {selectedOrder?.status?.toLowerCase() === "processing" && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<LocalShipping />}
              onClick={() => {
                setOpenDetails(false);
                setSelectedOrder(selectedOrder);
                handleOpenShipDialog();
              }}
              fullWidth={isMobile}
            >
              Assign Shipment
            </Button>
          )}
          {selectedOrder &&
            ["processing", "shipped", "delivered"].includes(
              selectedOrder.status?.toLowerCase(),
            ) && (
              <Button
                variant="contained"
                startIcon={<PictureAsPdf />}
                onClick={() => handleViewInvoice(selectedOrder.id)}
                disabled={loadingInvoice}
                fullWidth={isMobile}
                sx={{ color: "white" }}
              >
                {loadingInvoice ? "Loading..." : "View Invoice"}
              </Button>
            )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Orders;
